export interface FlightLeg {
  departureAirport: string
  departureAirportName: string
  departureCity: string
  departureTime: string
  departureDate: string
  arrivalAirport: string
  arrivalAirportName: string
  arrivalCity: string
  arrivalTime: string
  arrivalDate: string
  durationHours: number
  durationMinutes: number
  layovers: Array<{
    airportCode: string
    airportName: string
    city?: string
    durationHours?: number
    durationMinutes?: number
  }>
  airline: string
  airlineLogo: string
  flightNumber: string
  operatingAirline?: string
}

export interface FlightOption {
  leg: FlightLeg
  price: number
  pricePerPerson: number
  carrier: string
  carrierLogo: string
  carrierBrandMark?: string
  cabin: string
  journeyKey: string
  includedBaggage?: { carryOn?: string; checked?: string }
}

export interface FlightFare {
  outbound: FlightLeg
  inbound?: FlightLeg
  carrier: string
  carrierLogo: string
  carrierBrandMark?: string
  totalPrice: number
  pricePerPerson: number
  passengers?: number
  currencyCode: string
  cabin: string
  journeyKey: string
  includedBaggage?: { carryOn?: string; checked?: string }
  searchId?: string
  searchExpiry?: number
  searchParams?: string
}

export interface FlightUIData {
  type: 'flight'
  tripType: string
  searchQuery: string
  fares?: Array<FlightFare>
  outboundOptions?: Array<FlightOption>
  inboundOptions?: Array<FlightOption>
  passengers?: number
  currencyCode: string
  searchId?: string
  sessionId?: string
  searchExpiry?: number
  searchParams?: string
}

/**
 * Adapts raw backend contract data into a typed FlightUIData, isolating the
 * unsafe cast at this single boundary rather than scattering `as unknown as`
 * through component code.
 */
export function toFlightUIData(raw: unknown): FlightUIData {
  return normalizeFlightUIData(raw as FlightUIData)
}

/**
 * The backend may send flight data in either `outboundOptions`/`inboundOptions`
 * format or the older `fares` array format. This normalizes both into the
 * canonical `outboundOptions`/`inboundOptions` shape.
 */
export function normalizeFlightUIData(data: FlightUIData): FlightUIData {
  if (data.outboundOptions?.length) return data
  if (!data.fares?.length) return data

  const firstFare = data.fares[0]

  const outboundOptions: Array<FlightOption> = data.fares.map(fare => ({
    leg: fare.outbound,
    price: fare.totalPrice,
    pricePerPerson: fare.pricePerPerson,
    carrier: fare.carrier,
    carrierLogo: fare.carrierLogo,
    carrierBrandMark: fare.carrierBrandMark,
    cabin: fare.cabin,
    journeyKey: fare.journeyKey,
    includedBaggage: fare.includedBaggage,
  }))

  const inboundFares = data.fares.filter((fare): fare is FlightFare & { inbound: FlightLeg } => !!fare.inbound)
  const inboundOptions: Array<FlightOption> | undefined = inboundFares.length > 0
    ? inboundFares.map(fare => ({
        leg: fare.inbound,
        price: fare.totalPrice,
        pricePerPerson: fare.pricePerPerson,
        carrier: fare.carrier,
        carrierLogo: fare.carrierLogo,
        carrierBrandMark: fare.carrierBrandMark,
        cabin: fare.cabin,
        journeyKey: fare.journeyKey,
        includedBaggage: fare.includedBaggage,
      }))
    : undefined

  return {
    ...data,
    outboundOptions,
    inboundOptions,
    currencyCode: data.currencyCode || firstFare.currencyCode,
    passengers: data.passengers ?? firstFare.passengers,
    searchId: data.searchId ?? firstFare.searchId,
    sessionId: data.sessionId,
    searchExpiry: data.searchExpiry ?? firstFare.searchExpiry,
    searchParams: data.searchParams ?? firstFare.searchParams,
  }
}

/**
 * Builds a partial App.JourneyV2 from a chat FlightOption. Only the properties
 * consumed by DrawerFlightCard and ChatFlightSearchContent are populated:
 *   id, carrierName, carrierLogo, carrierBrandMark, currencyCode,
 *   price.adult.totalFare, flightGroup (flights, totalFlightDuration,
 *   numberOfStops), bookingInfo (journeyKey, providerSearchId, boookingKey,
 *   bookingClass), fareType.
 */
export function optionToJourney(option: FlightOption, id: string, currencyCode: string): App.JourneyV2 {
  const { leg } = option

  const mainFlight = {
    departureAirport: leg.departureAirport,
    arrivalAirport: leg.layovers.length > 0 ? leg.layovers[0].airportCode : leg.arrivalAirport,
    departureAirportName: leg.departureAirportName,
    arrivalAirportName: leg.arrivalAirportName,
    departingDate: leg.departureDate,
    arrivalDate: leg.arrivalDate,
    departingTime: leg.departureTime,
    arrivalTime: leg.arrivalTime,
    departureDateTime: `${leg.departureDate}T${leg.departureTime}:00`,
    arrivalDateTime: `${leg.arrivalDate}T${leg.arrivalTime}:00`,
    flightNumber: leg.flightNumber,
    stopoverTime: leg.layovers[0]
      ? { hours: leg.layovers[0].durationHours ?? 0, minutes: leg.layovers[0].durationMinutes ?? 0 }
      : { hours: 0, minutes: 0 },
  }

  const flights = [mainFlight]

  for (let i = 0; i < leg.layovers.length; i++) {
    const layover = leg.layovers[i]
    const nextLayover = leg.layovers[i + 1]
    const nextAirport = nextLayover ? nextLayover.airportCode : leg.arrivalAirport

    flights.push({
      departureAirport: layover.airportCode,
      arrivalAirport: nextAirport,
      departureAirportName: layover.airportName,
      arrivalAirportName: i === leg.layovers.length - 1 ? leg.arrivalAirportName : (nextLayover?.airportName ?? ''),
      departingDate: leg.departureDate,
      arrivalDate: i === leg.layovers.length - 1 ? leg.arrivalDate : leg.departureDate,
      departingTime: '',
      arrivalTime: i === leg.layovers.length - 1 ? leg.arrivalTime : '',
      departureDateTime: '',
      arrivalDateTime: i === leg.layovers.length - 1 ? `${leg.arrivalDate}T${leg.arrivalTime}:00` : '',
      flightNumber: '',
      stopoverTime: nextLayover
        ? { hours: nextLayover.durationHours ?? 0, minutes: nextLayover.durationMinutes ?? 0 }
        : { hours: 0, minutes: 0 },
    })
  }

  return {
    id,
    carrierName: option.carrier,
    carrierLogo: option.carrierLogo,
    carrierBrandMark: option.carrierBrandMark,
    currencyCode,
    price: { adult: { totalFare: option.pricePerPerson } },
    flightGroup: {
      flights: flights as Array<App.FlightV2>,
      totalFlightDuration: { hours: leg.durationHours, minutes: leg.durationMinutes },
      numberOfStops: leg.layovers.length,
    },
    bookingInfo: {
      journeyKey: option.journeyKey,
      providerSearchId: '',
      boookingKey: option.journeyKey,
      bookingClass: option.cabin,
    },
    fareType: option.cabin,
  } as App.JourneyV2
}
